/**
 * useDpiPolling — polling ~1s de GET /api/v1/dpi (US-VDPI-06, UX-2=A).
 *
 * KISS : pas de WebSocket (D-08 réserve WS au P2). setInterval natif + fetch
 * authentifié (apiClient attache le JWT). Robuste cross-onglet/écran car la
 * synchro passe par le backend, pas par un store frontend.
 *
 * - cleanup à l'unmount (clearInterval + flag annulation)
 * - pas de requêtes qui se chevauchent (garde `inFlight`)
 * - une erreur transitoire ne casse pas l'affichage : on garde la dernière vue
 *   connue et on n'expose `error` que tant qu'aucune donnée n'a été reçue.
 */

import { useEffect, useRef, useState } from 'react';
import { dpiService } from '@/shared/services/dpiService';
import type { DpiView } from '@/shared/types/dpi';

const POLL_INTERVAL_MS = 1000;

export interface DpiPollingState {
  data: DpiView | null;
  loading: boolean;
  error: string | null;
}

export function useDpiPolling(intervalMs: number = POLL_INTERVAL_MS): DpiPollingState {
  const [data, setData] = useState<DpiView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs pour ne pas re-créer l'intervalle ni dépendre de l'état dans le tick.
  const cancelledRef = useRef(false);
  const inFlightRef = useRef(false);
  const hasDataRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    async function tick(): Promise<void> {
      if (inFlightRef.current || cancelledRef.current) return;
      inFlightRef.current = true;
      try {
        const view = await dpiService.getDpi();
        if (cancelledRef.current) return;
        hasDataRef.current = true;
        setData(view);
        setError(null);
      } catch (e) {
        if (cancelledRef.current) return;
        // On n'affiche l'erreur que si on n'a jamais rien reçu (1er chargement).
        if (!hasDataRef.current) {
          setError(e instanceof Error ? e.message : 'Erreur de chargement DPI');
        }
      } finally {
        inFlightRef.current = false;
        if (!cancelledRef.current) setLoading(false);
      }
    }

    void tick(); // 1er fetch immédiat
    const id = setInterval(() => void tick(), intervalMs);

    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, [intervalMs]);

  return { data, loading, error };
}
